import { FileText, Settings, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ElementType;
}

const steps: Step[] = [
  {
    id: 1,
    title: "Upload Contract",
    description: "Upload and provide basic info",
    icon: FileText,
  },
  {
    id: 2,
    title: "Configure Review",
    description: "Set jurisdiction and role",
    icon: Settings,
  },
];

interface StepIndicatorProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
}

export function StepIndicator({ currentStep, onStepClick }: StepIndicatorProps) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-2 sm:gap-4">
        {steps.map((step, index) => {
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const isLast = index === steps.length - 1;

          return (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => onStepClick?.(step.id)}
                disabled={!isCompleted && !isActive}
                className={cn(
                  "flex items-center gap-2 rounded-xl px-2 py-2 transition-all duration-300 sm:gap-3 sm:px-4 sm:py-3",
                  isActive
                    ? "border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] dark:bg-primary/20 dark:border-primary/50"
                    : isCompleted
                    ? "cursor-pointer border border-[rgba(15,23,42,0.09)] bg-[var(--surface-sunken)] hover:bg-[var(--lm-hover)] dark:bg-slate-800/50 dark:border-slate-700 dark:hover:bg-slate-800"
                    : "cursor-not-allowed border border-[rgba(15,23,42,0.09)] bg-[var(--surface-sunken)] opacity-50 dark:bg-slate-900/50 dark:border-slate-800"
                )}
              >
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                      : isCompleted
                      ? "bg-[var(--tint-green-bg)] text-[#027a48] dark:bg-emerald-500/20 dark:text-emerald-400"
                      : "bg-[var(--surface-sunken)] text-[#576274] dark:bg-slate-800 dark:text-slate-500"
                  )}
                >
                  <step.icon className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <p
                    className={cn(
                      "text-sm font-semibold transition-colors",
                      isActive
                        ? "text-primary"
                        : isCompleted
                        ? "text-[var(--lm-foreground-strong)] dark:text-slate-200"
                        : "text-[var(--lm-muted)] dark:text-slate-500"
                    )}
                  >
                    {step.title}
                  </p>
                  <p className="hidden text-xs text-[var(--lm-muted)] dark:text-slate-500 sm:block">{step.description}</p>
                </div>
              </button>

              {!isLast && (
                <div className="mx-1 flex items-center sm:mx-4">
                  <ChevronRight
                    className={cn(
                      "h-5 w-5 transition-colors",
                      isCompleted ? "text-[#027a48] dark:text-emerald-500" : "text-[var(--lm-muted)] dark:text-slate-700"
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface StepNavigationProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  isNextDisabled?: boolean;
  nextLabel?: string;
}

export function StepNavigation({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  isNextDisabled = false,
  nextLabel = "Continue",
}: StepNavigationProps) {
  return (
    <div className="mt-6 flex items-center justify-between gap-4 border-t border-[var(--lm-border-subtle)] pt-6 dark:border-slate-800/50">
      <Button
        type="button"
        variant="ghost"
        onClick={onBack}
        disabled={currentStep === 1}
        aria-label="Back"
        className={cn(
          "gap-2 max-sm:h-10 max-sm:w-10 max-sm:gap-0 max-sm:p-0",
          currentStep === 1 && "pointer-events-none opacity-0"
        )}
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="max-sm:hidden">Back</span>
      </Button>

      <div className="flex items-center gap-2">
        <span className="text-sm text-[var(--lm-muted)] dark:text-slate-500">
          Step {currentStep} of {totalSteps}
        </span>
      </div>

      <Button
        type="button"
        variant="default"
        onClick={onNext}
        disabled={isNextDisabled}
        aria-label={nextLabel}
        className="gap-2 px-6 max-sm:h-10 max-sm:w-10 max-sm:gap-0 max-sm:p-0"
      >
        <span className="max-sm:hidden">{nextLabel}</span>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
